import React, { useEffect } from "react";
import { AnimatePresence, motion as m } from "framer-motion";
import EditorialCover from "./EditorialCover";
import { Tag, Button } from "./ui";

const Field = ({ label, value }) =>
  value ? (
    <div>
      <p className="t-eyebrow">{label}</p>
      <p className="mt-2 text-sm font-medium">{value}</p>
    </div>
  ) : null;

const Chapter = ({ index, title, body }) =>
  body ? (
    <div className="border-t border-[var(--color-border)] pt-7">
      <p className="t-eyebrow">
        <span className="text-electric">{index}</span> {title}
      </p>
      <p className="mt-4 max-w-prose text-[15px] leading-[1.8] text-ink-muted dark:text-slightwhite/70">
        {body}
      </p>
    </div>
  ) : null;

const ProjectDetails = ({ detail, setView }) => {
  const open = Boolean(detail);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setView(false);
    document.body.style.overflow = "hidden";
    // Lenis listens on window, so native overflow alone will not hold it.
    if (window.__lenis) window.__lenis.stop();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, setView]);

  const {
    title,
    category,
    year,
    role,
    client,
    summary,
    problem,
    approach,
    outcome,
    highlights = [],
    stack = [],
    images = [],
    links = {},
    accent,
  } = detail || {};

  return (
    <AnimatePresence>
      {open && (
        <m.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-azure-dark/70 backdrop-blur-sm"
        onClick={() => setView(false)}
      >
        <m.div
          key="panel"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`${title} case study`}
          data-lenis-prevent
          className="absolute inset-x-0 bottom-0 top-10 overflow-y-auto overscroll-contain rounded-t-xl bg-[var(--color-bg)] md:top-16"
        >
          {/* Sticky bar */}
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--color-border)] bg-paper/90 px-[var(--gutter)] py-4 backdrop-blur-xl dark:bg-night/90">
            <p className="t-eyebrow">
              {category} — {year}
            </p>
            <button
              onClick={() => setView(false)}
              aria-label="Close case study"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] transition-colors duration-300 hover:border-azure hover:text-azure dark:hover:border-electric dark:hover:text-electric"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none">
                <line x1="5" y1="5" x2="19" y2="19" />
                <line x1="19" y1="5" x2="5" y2="19" />
              </svg>
            </button>
          </div>

          <div className="mx-auto w-full max-w-content px-[var(--gutter)] pb-24">
            {/* Masthead */}
            <div className="grid gap-10 border-b border-[var(--color-border)] py-12 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h2 className="t-hero max-w-2xl">{title}</h2>
                <p className="t-lead mt-6 max-w-prose">{summary}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  {links.live && (
                    <Button href={links.live} size="sm" withArrow>
                      Visit site
                    </Button>
                  )}
                  {links.repo && (
                    <Button href={links.repo} variant="outline" size="sm" withArrow>
                      Source
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 self-start lg:col-span-5 lg:grid-cols-2">
                <Field label="Role" value={role} />
                <Field label="Client" value={client} />
                <Field label="Year" value={year} />
                <Field label="Discipline" value={category} />
              </div>
            </div>

            {/* Lead image */}
            <div className="mt-12 overflow-hidden rounded-lg bg-paper-warm dark:bg-night-surface">
              {images[0] ? (
                <img src={images[0]} alt={title} className="w-full object-cover" />
              ) : (
                <div className="aspect-[16/9]">
                  <EditorialCover title={title} kicker={category} accent={accent} />
                </div>
              )}
            </div>

            {/* Narrative */}
            <div className="mt-14 grid gap-12 lg:grid-cols-12">
              <div className="space-y-9 lg:col-span-8">
                <Chapter index="01" title="The problem" body={problem} />
                <Chapter index="02" title="The approach" body={approach} />
                <Chapter index="03" title="The outcome" body={outcome} />
              </div>

              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-8 rounded-lg border border-[var(--color-border)] bg-paper-warm p-7 dark:bg-night-surface">
                  {highlights.length > 0 && (
                    <div>
                      <p className="t-eyebrow">What it involved</p>
                      <ul className="mt-4 space-y-3">
                        {highlights.map((h) => (
                          <li key={h} className="flex gap-3 text-sm leading-relaxed">
                            <span className="mt-[9px] inline-block h-1 w-1 shrink-0 rounded-full bg-electric" />
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {stack.length > 0 && (
                    <div>
                      <p className="t-eyebrow">Built with</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {stack.map((s) => (
                          <Tag key={s} variant="azure">
                            {s}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>

            {/* Gallery */}
            {images.length > 1 && (
              <div className="mt-16">
                <p className="t-eyebrow">Selected screens</p>
                <div className="mt-6 columns-1 gap-5 sm:columns-2 lg:columns-3 [&>*]:mb-5">
                  {images.slice(1).map((src, i) => (
                    <img
                      key={src}
                      src={src}
                      alt={`${title} — view ${i + 2}`}
                      loading="lazy"
                      className="w-full break-inside-avoid rounded-md"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectDetails;
